import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { LoadingState } from "@/components/base/LoadingState";
import { IntegrationsNav } from "@/components/feature/IntegrationsNav";
import { fmtDateTime } from "@/lib/format";
import {
  processSyncJobs,
  retrySyncJob,
  type SyncJobRow,
  type DomainEventRow,
} from "@/lib/integrations";
import {
  collectCatalogIds,
  emptySyncEventsCatalog,
  extractJobRefs,
  matchesOperatorSearch,
  presentDomainEvent,
  presentSyncJob,
  sellableLookupKey,
  type ListingIdentity,
  type ProductIdentity,
  type SyncEventsCatalog,
  type VariantIdentity,
} from "@/lib/sync-events-ux";

const eventFilters = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "processed", label: "Processed" },
  { value: "failed", label: "Failed" },
];

function eventStatus(status: string) {
  if (status === "processed") return <StatusBadge status="success" customLabel="Processed" />;
  if (status === "failed") return <StatusBadge status="error" customLabel="Failed" />;
  return <StatusBadge status="pending" />;
}

function jobStatus(status: string, errorClass?: string | null) {
  if (status === "succeeded") return <StatusBadge status="success" customLabel="Succeeded" />;
  if (status === "processing") return <StatusBadge status="info" customLabel="Processing" />;
  if (status === "retrying") return <StatusBadge status="warning" customLabel="Retrying" />;
  if (status === "queued") return <StatusBadge status="pending" customLabel="Queued" />;
  if (status === "failed") {
    const label = errorClass === "not_configured" ? "Not configured" : "Failed";
    return <StatusBadge status={errorClass === "not_configured" ? "not_configured" : "error"} customLabel={label} />;
  }
  return <StatusBadge status="pending" />;
}

function TechnicalDetails({ fields }: { fields: { label: string; value: string }[] }) {
  return (
    <details className="mt-1.5">
      <summary className="text-[11px] text-slate-400 cursor-pointer hover:text-slate-600">Details</summary>
      <dl className="mt-1.5 space-y-0.5 text-[11px] text-slate-400 font-mono break-all">
        {fields.map((f) => (
          <div key={f.label}>
            <dt className="inline text-slate-300">{f.label}: </dt>
            <dd className="inline">{f.value}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

async function loadSyncEventsCatalog(
  orgId: string,
  jobs: SyncJobRow[],
  events: DomainEventRow[],
  connectors: { id: string; name: string; provider: string }[],
): Promise<SyncEventsCatalog> {
  const ids = collectCatalogIds(jobs, events);
  const catalog = emptySyncEventsCatalog();
  for (const c of connectors) {
    catalog.connectors[c.id] = { id: c.id, name: c.name, provider: c.provider };
  }

  const fetches: Promise<void>[] = [];

  if (ids.productIds.length) {
    fetches.push(
      (async () => {
        const { data } = await supabase
          .from("products")
          .select("id, name")
          .eq("org_id", orgId)
          .in("id", ids.productIds);
        for (const row of (data ?? []) as ProductIdentity[]) catalog.products[row.id] = row;
      })(),
    );
  }

  if (ids.variantIds.length) {
    fetches.push(
      (async () => {
        const { data } = await supabase
          .from("variants")
          .select("id, product_id, sku, option_values, selling_price")
          .eq("org_id", orgId)
          .in("id", ids.variantIds);
        for (const row of (data ?? []) as VariantIdentity[]) catalog.variants[row.id] = row;
      })(),
    );
  }

  if (ids.listingIds.length) {
    fetches.push(
      (async () => {
        const { data } = await supabase
          .from("channel_listings")
          .select("id, connector_id, product_id, price_strategy, provider_config, included_variant_count")
          .eq("org_id", orgId)
          .in("id", ids.listingIds);
        for (const row of (data ?? []) as ListingIdentity[]) catalog.listings[row.id] = row;
      })(),
    );
    fetches.push(
      (async () => {
        const { data } = await supabase
          .from("channel_listing_variants")
          .select("channel_listing_id, variant_id, included, effective_channel_price, variants(selling_price)")
          .eq("org_id", orgId)
          .in("channel_listing_id", ids.listingIds);
        for (const row of data ?? []) {
          const nested = row.variants as { selling_price?: number | null } | { selling_price?: number | null }[] | null;
          const selling =
            Array.isArray(nested) ? nested[0]?.selling_price ?? null : nested?.selling_price ?? null;
          catalog.listingVariantPrices.push({
            listing_id: String(row.channel_listing_id),
            variant_id: String(row.variant_id),
            included: Boolean(row.included),
            effective_channel_price: row.effective_channel_price == null ? null : Number(row.effective_channel_price),
            selling_price: selling == null ? null : Number(selling),
          });
        }
      })(),
    );
  }

  if (ids.orderIds.length) {
    fetches.push(
      (async () => {
        const [{ data: orders }, { data: items }] = await Promise.all([
          supabase.from("sales_orders").select("id, order_number").eq("org_id", orgId).in("id", ids.orderIds),
          supabase.from("sales_order_items").select("sales_order_id, product_id, product_name").eq("org_id", orgId).in("sales_order_id", ids.orderIds),
        ]);
        const namesByOrder: Record<string, string[]> = {};
        for (const it of items ?? []) {
          const oid = String(it.sales_order_id);
          const snapshot = typeof it.product_name === "string" ? it.product_name.trim() : "";
          const fromCatalog = it.product_id ? catalog.products[String(it.product_id)]?.name : null;
          const name = snapshot || fromCatalog || "";
          if (!name) continue;
          namesByOrder[oid] = namesByOrder[oid] ?? [];
          if (!namesByOrder[oid].includes(name)) namesByOrder[oid].push(name);
        }
        for (const row of (orders ?? []) as { id: string; order_number: string | null }[]) {
          catalog.orders[row.id] = {
            id: row.id,
            order_number: row.order_number,
            product_names: namesByOrder[row.id] ?? [],
          };
        }
      })(),
    );
  }

  await Promise.all(fetches);

  const inventoryPairs = jobs
    .filter((j) => j.job_type === "inventory_publish")
    .map((j) => {
      const refs = extractJobRefs(j);
      return refs.listingId && refs.variantId ? { listingId: refs.listingId, variantId: refs.variantId } : null;
    })
    .filter((row): row is { listingId: string; variantId: string } => Boolean(row));
  const uniquePairs = [...new Map(inventoryPairs.map((p) => [`${p.listingId}:${p.variantId}`, p])).values()].slice(0, 25);
  await Promise.all(
    uniquePairs.map(async (pair) => {
      const { data } = await supabase.rpc("get_listing_sellable_quantity", {
        p_org_id: orgId,
        p_listing_id: pair.listingId,
        p_variant_id: pair.variantId,
      });
      if (typeof data === "number" && Number.isFinite(data)) {
        catalog.sellableByListingVariant[sellableLookupKey(pair.listingId, pair.variantId)] = data;
      }
    }),
  );

  return catalog;
}

export default function Automation() {
  const { org } = useAuth();
  const queryClient = useQueryClient();
  const [eventFilter, setEventFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [processing, setProcessing] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState("");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["automation", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const [events, jobs, connectors] = await Promise.all([
        supabase
          .from("domain_events")
          .select("id, event_type, aggregate_type, aggregate_id, payload, status, error, created_at, processed_at")
          .eq("org_id", org!.id)
          .order("created_at", { ascending: false })
          .limit(100),
        supabase
          .from("sync_jobs")
          .select("id, connector_id, job_type, status, payload, result, error_message, error_class, attempts, max_attempts, idempotency_key, next_retry_at, entity_type, entity_id, started_at, completed_at, created_at")
          .eq("org_id", org!.id)
          .order("created_at", { ascending: false })
          .limit(100),
        supabase.from("connectors").select("id, name, provider").eq("org_id", org!.id),
      ]);
      const eventRows = (events.data ?? []) as DomainEventRow[];
      const jobRows = (jobs.data ?? []) as SyncJobRow[];
      const connectorRows = (connectors.data ?? []) as { id: string; name: string; provider: string }[];
      const catalog = await loadSyncEventsCatalog(org!.id, jobRows, eventRows, connectorRows);
      return { events: eventRows, jobs: jobRows, catalog };
    },
  });

  const handleProcess = async () => {
    setProcessing(true);
    setFeedback("");
    try {
      const res = await processSyncJobs();
      const notConfig = res.results?.filter((r) => r.errorClass === "not_configured").length ?? 0;
      setFeedback(
        `Worker processed ${res.processed} job(s) — ${
          notConfig > 0 ? `${notConfig} not configured (requires credentials), ` : ""
        }${res.results?.filter((r) => r.ok).length ?? 0} succeeded.`,
      );
    } catch (err) {
      setFeedback((err as Error).message);
    }
    setProcessing(false);
    queryClient.invalidateQueries({ queryKey: ["automation", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["connectors", org?.id] });
  };

  const handleRetry = async (jobId: string) => {
    setRetryingId(jobId);
    setFeedback("");
    try {
      const queued = await retrySyncJob(jobId);
      if (!queued.ok) {
        setFeedback(queued.error ?? "Unable to retry this job.");
        setRetryingId(null);
        return;
      }
      const res = await processSyncJobs();
      setFeedback(`Job re-queued — worker processed ${res.processed} job(s).`);
    } catch (err) {
      setFeedback((err as Error).message);
    }
    setRetryingId(null);
    queryClient.invalidateQueries({ queryKey: ["automation", org?.id] });
  };

  const catalog = data?.catalog ?? emptySyncEventsCatalog();
  const presentedJobs = useMemo(
    () => (data?.jobs ?? []).map((j) => ({ job: j, view: presentSyncJob(j, catalog) })),
    [data?.jobs, catalog],
  );
  const presentedEvents = useMemo(
    () => (data?.events ?? []).map((e) => ({ event: e, view: presentDomainEvent(e, catalog) })),
    [data?.events, catalog],
  );

  const filteredJobs = presentedJobs.filter((row) => matchesOperatorSearch(row.view.searchText, query));
  const filteredEvents = presentedEvents.filter((row) => {
    if (eventFilter !== "all" && row.event.status !== eventFilter) return false;
    return matchesOperatorSearch(row.view.searchText, query);
  });

  const queuedCount = (data?.jobs ?? []).filter((j) => j.status === "queued" || j.status === "pending" || j.status === "retrying").length;
  const notConfiguredCount = (data?.jobs ?? []).filter((j) => j.error_class === "not_configured").length;

  return (
    <div>
      <PageHeader title="Sync & Events" subtitle="Sync jobs, the event outbox, and connector workers">
        <button
          onClick={handleProcess}
          disabled={processing || queuedCount === 0}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-play-circle-line" />
          {processing ? "Processing..." : `Run worker (${queuedCount} queued)`}
        </button>
      </PageHeader>

      {feedback && (
        <div className="mb-4 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 flex items-center gap-2">
          <i className="ri-information-line text-slate-400" />
          {feedback}
        </div>
      )}

      {notConfiguredCount > 0 && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-100 rounded-lg text-sm text-amber-700 flex items-center gap-2">
          <i className="ri-alert-line" />
          {notConfiguredCount} sync job(s) can't run because their connector has no credentials. Configure credentials to
          enable live marketplace sync.
        </div>
      )}

      <div className="mb-4">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          placeholder="Search product, SKU, order number, connector, or job type"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">Sync Jobs</h3>
              <span className="text-xs text-slate-400">{filteredJobs.length} jobs</span>
            </div>
            {isLoading ? (
              <div className="p-8">
                <LoadingState />
              </div>
            ) : isError ? (
              <div className="p-8 text-center">
                <p className="text-sm text-slate-500 mb-4">Couldn't load sync jobs.</p>
                <button
                  onClick={() => refetch()}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap"
                >
                  Retry
                </button>
              </div>
            ) : filteredJobs.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">
                {query.trim() ? "No jobs match this search" : "No sync jobs yet"}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50/70 border-b border-slate-100">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Work</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Attempts</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">When</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredJobs.map(({ job: j, view }) => (
                      <tr key={j.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors align-top">
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-slate-800">{view.productName}</p>
                          {view.fallbackIdLine && <p className="text-xs text-slate-400 mt-0.5">{view.fallbackIdLine}</p>}
                          {view.variantLine && <p className="text-xs text-slate-600 mt-0.5">{view.variantLine}</p>}
                          {view.skuLine && <p className="text-xs text-slate-500 mt-0.5">{view.skuLine}</p>}
                          <p className="text-xs text-slate-500 mt-1.5">{view.workLabel}</p>
                          <p className="text-xs text-slate-500">{view.channelLine}</p>
                          {view.domainsLabel && <p className="text-[11px] text-slate-400">Domains: {view.domainsLabel}</p>}
                          {view.sellableLine && <p className="text-xs text-slate-600 mt-0.5">{view.sellableLine}</p>}
                          {view.priceLine && <p className="text-xs text-slate-600 mt-0.5">{view.priceLine}</p>}
                          <TechnicalDetails fields={view.technical} />
                        </td>
                        <td className="px-4 py-3">
                          {jobStatus(j.status, j.error_class)}
                          {view.errorLine && (
                            <p className="text-xs text-red-600 mt-1 max-w-[240px] whitespace-normal">{view.errorLine}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {j.attempts}/{j.max_attempts}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          <p>Created {fmtDateTime(j.created_at)}</p>
                          {j.completed_at && (
                            <p className="text-xs text-slate-400 mt-0.5">
                              {j.status === "failed" ? "Failed" : "Completed"} {fmtDateTime(j.completed_at)}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {j.status === "failed" && j.error_class !== "not_configured" && j.error_class !== "permanent" && (
                            <button
                              onClick={() => handleRetry(j.id)}
                              disabled={retryingId === j.id || j.attempts >= j.max_attempts}
                              className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-xs font-medium rounded-lg whitespace-nowrap inline-flex items-center gap-1"
                            >
                              <i className="ri-refresh-line" />
                              {retryingId === j.id ? "Retrying..." : "Retry"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Event Outbox</h3>
              <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1">
                {eventFilters.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setEventFilter(f.value)}
                    className={`flex-1 px-2 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                      eventFilter === f.value ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            {isLoading ? (
              <div className="p-8">
                <LoadingState />
              </div>
            ) : filteredEvents.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">No events in this state</div>
            ) : (
              <div className="divide-y divide-slate-50 max-h-[520px] overflow-y-auto">
                {filteredEvents.map(({ event: e, view }) => (
                  <div key={e.id} className="px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-slate-700">{view.eventType}</span>
                      {eventStatus(e.status)}
                    </div>
                    {view.headline && <p className="text-sm text-slate-800 mt-1">{view.headline}</p>}
                    {view.lines.map((line) => (
                      <p key={line} className="text-xs text-slate-500 mt-0.5">
                        {line}
                      </p>
                    ))}
                    {view.errorLine && <p className="text-xs text-red-600 mt-1">{view.errorLine}</p>}
                    <p className="text-[11px] text-slate-300 mt-1">{fmtDateTime(e.created_at)}</p>
                    <TechnicalDetails fields={view.technical} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
